import React, { useState, useEffect, useMemo, ReactNode, Dispatch, SetStateAction } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import { history, useDispatch } from 'umi';
import CardLineChart from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/CardLineChart';
import {
  getCompanyDetailsPath,
  getMetricTitle,
  getDetailGamePathCommon,
} from '@/pages/intelligence/utils/topchartsUtils';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import PheatChart from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/PheatChart';
import CardTitleContent from './components/CardTitleContent';
import CardValueContent from './components/CardValueContent';
import useOverviewCardHooks from './useOverviewCardHooks';
import {
  METRICS_NAMES_LOCALE_KEYS_MAP,
  GSD_METRICS_NAMES_LOCALE_KEYS_MAP,
  STEAM_TOP_SELLERS_NAMES_LOCALE_KEYS_MAP,
  STEAM_TOP_WISH_LISTS_NAMES_LOCALE_KEYS_MAP,
  CUSTOM_OVERVIEW_CARD_NAMES_LOCALE_KEYS_MAP,
  USREVENUE_CARD_NAMES_LOCALE_KEYS_MAP,
  JUMPTO_KPITRENDS_MAMP,
  JUMPTO_PCCONSOLESTORERANK_MAMP,
  JUMPTO_KPITRENDS_APMERE_MAMP,
} from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/CardContext/constants';
import { RATIOVALUEFORMATOVERVIEWCARDMETRIC } from '@/pages/intelligence/components/OverviewCard/utils/const';
// import { Progress, Row, Col } from 'tea-component';
// import { financeFormat } from '@/pages/home/utils/common';
import { toDateTypeFilter } from '@/pages/intelligence/utils/common';
import CardPlatformContent from '@intelligence/components/OverviewCard/components/CardPlatformContent';
import './index.scss';

/**
 * 特殊页面跳转参数
 */
export interface ISpecialJumpParams {
  /**
   * 日期粒度（跳转到目标页面时应用的）
   */
  conditionDate?: 'day' | 'week' | 'month';
}

interface IOverviewCardProps {
  // y轴是否反转，默认不反转
  yAxisInverse?: boolean;
  // y轴最小值，默认undefined
  yAxisMin?: number;
  // y轴刻度文案formatter
  yAxisFormatter?: (payload?: number) => any;
  // 是否开启折线图阴影堆叠，默认开启
  enableSeriesAreaStyle?: boolean;
  // 无数据文案
  noDataText?: string;
  className?: string;
  titleIcon: string;
  isShowDataSourceIcon?: boolean;
  titleContent?: ReactNode;
  baseIndicators?: any;
  numArrowData?: any;
  lineData?: any;
  lineDom?: ReactNode;
  rankData?: any;
  data_source: string;
  index?: number;
  moduleName?: string;
  isPolishing?: boolean;
  entityType?: string;
  rankLoading?: boolean;
  isTrueActiveSegmentCard?: boolean;
  gameId?: string;
  gameName?: string;
  cardItem?: any;
  customBubbleContent?: ReactNode;
  aggregationBubbleWidth?: number;
  isShowInfoIcon?: boolean;
  tipsInfoText?: ReactNode;
  isAdustTooltipPosition?: boolean;
  isOperateRegion?: boolean;
  conditionDate?: string;
  customCardContent?: () => ReactNode;
  ifShowPlatform?: boolean;
  conditionMarket?: { regionType: string; regions: string[] };
  isShowRateTips?: boolean;
  setActiveId: Dispatch<SetStateAction<String>>;
  setActiveSegment: Dispatch<SetStateAction<String>>;
  // 直接跳转 url
  link_url?: string;
  // 点击跳转方法
  onClickJump?: () => void;
  // 跳转链接上的查询参数
  query?: Record<string, string>;
  // 是否显示 Rank 区域，默认显示
  isShowRank?: boolean;
  /** 全部展示 x 轴标签的断点宽度（默认 1440） */
  fullLabelBreakpoint?: number;
  /**
   * 特殊页面跳转参数
   */
  specialJumpParams?: ISpecialJumpParams;
  burriedPointLogging?: (item: any, gameId: string, gameName: string) => void;
}

// overview 卡片新组件
const OverviewCard = (props: IOverviewCardProps) => {
  const {
    yAxisInverse,
    yAxisMin,
    yAxisFormatter,
    enableSeriesAreaStyle,
    noDataText,
    className,
    titleIcon,
    isShowDataSourceIcon,
    titleContent: titleContentTemp,
    baseIndicators,
    numArrowData,
    lineData,
    lineDom,
    rankData,
    data_source,
    index,
    moduleName,
    isPolishing,
    entityType,
    rankLoading,
    gameId,
    gameName,
    cardItem = {},
    customBubbleContent,
    aggregationBubbleWidth,
    isShowInfoIcon,
    tipsInfoText,
    isAdustTooltipPosition,
    isOperateRegion,
    conditionDate,
    customCardContent,
    ifShowPlatform = false,
    conditionMarket,
    isShowRateTips,
    setActiveId,
    setActiveSegment,
    link_url: linkUrl,
    onClickJump,
    isTrueActiveSegmentCard = false, // 是否为特殊卡片，特殊卡片指标，跳转不传setTopchartJumpParam
    query,
    isShowRank,
    specialJumpParams,
    burriedPointLogging,
    fullLabelBreakpoint,
  } = props;

  const dispatch = useDispatch();

  const { detailFilterExtend, setDetailFilterExtend, filterExtend, setFilterExtend } =
    useFilterExtend({});
  const [noDataCondition, setNoDataCondition] = useState(true);

  const { formatMessage } = useIntlExtend();

  const { metricTitleLocaleKey, titleContentValue: titleContent } = useOverviewCardHooks({
    titleContentTemp,
    moduleName,
    cardItem,
    isOperateRegion,
  });

  // const ifShowPlatform = useMemo(() => {
  //   const platformList = cardItem?.platformList || [];
  //   if (
  //     Array.isArray(platformList) ||
  //     platformList?.length < 1 ||
  //     (platformList?.length === 1 && platformList[0]?.platform === 'all')
  //   ) {
  //     return false;
  //   }
  //   return true;
  // }, [cardItem]);

  const platformListCur = useMemo(() => cardItem?.platformList || [], [cardItem]);

  // 需要转百分比的卡片
  const isRatioValueFormat = useMemo(
    () => RATIOVALUEFORMATOVERVIEWCARDMETRIC?.includes(titleContent),
    [titleContent],
  );
  // 需要转百分比的卡片，但不需要*100
  // const isRatioValueFormatNoPrecent = useMemo(() => ['D1','D7'].includes(titleContent), [titleContent]);

  useEffect(() => {
    const condition =
      (JSON.stringify(baseIndicators) === '{}' || baseIndicators === undefined) &&
      (JSON.stringify(numArrowData) === '{}' || numArrowData === undefined) &&
      (JSON.stringify(lineData) === '{}' || lineData === undefined);

    const conditionOther =
      (baseIndicators?.indicatorsVal === null || baseIndicators?.indicatorsVal === '') &&
      // baseIndicators?.rankVal === 0 &&
      (baseIndicators?.latestDate === '' || baseIndicators?.latestDate === 'Invalid date') &&
      JSON.stringify(lineData?.seriesData) === '[]' &&
      JSON.stringify(lineData?.xAxisData) === '[]';

    // 公司详情页 Summary 模块特有的 无数据判断条件
    const companyDetailsSummaryCondition =
      moduleName === 'companies' &&
      baseIndicators?.indicatorsVal === 0 &&
      baseIndicators?.rankVal === '-';

    // marketProfile Summary 模块特有的 无数据判断条件
    const marketProfileDetailsSummaryCondition =
      moduleName === 'marketProfile' &&
      baseIndicators?.indicatorsVal === '-' &&
      baseIndicators?.rankVal === 0;

    const initCondition =
      condition ||
      conditionOther ||
      companyDetailsSummaryCondition ||
      marketProfileDetailsSummaryCondition;

    setNoDataCondition(initCondition);
  }, [titleIcon, titleContent, baseIndicators, rankData, lineData, numArrowData]);

  const icon = data_source === 'ogdb' ? 'databrain' : titleIcon; // 卡片的数据源图标以及 hover 数据源提示，优先以 summary 接口的 data_source 字段为准（ogdb）
  // 判断数值是否为空数组 - 是否展示折线图
  const isShowLine = !(
    JSON.stringify(lineData?.seriesData) === '[]' || JSON.stringify(lineData?.xAxisData) === '[]'
  );

  const getStatsActiveTabMobile = (metric: string): string => {
    const activeTabsMapping: {
      [key: string]: string;
    } = {
      DAU: 'activeusers',
      MAU: 'activeusers',
      WAU: 'activeusers',
      'Revenue($)': 'revenue',
      RetentionD2: 'retention_monthly',
      HoursWatched: 'hoursWatched',
      PeakCCV: 'peackCCV',
    };
    return activeTabsMapping?.[metric] ?? metric?.toLocaleLowerCase() ?? activeTabsMapping.DAU;
  };

  const pcConsoleActiveTabsMapping: {
    [key: string]: string;
  } = {
    MAU: 'mau',
    'DigitalRevenue($)': 'digitalRevenue',
    'DigitalARPPU($)': 'digitalRevenue',
    'PhysicalRevenue($)': 'physicalRevenue',
    HoursWatched: 'hoursWatched',
    PeakCCV: 'peackCCV',
    SteamPCU: 'pcu',
    SteamUnits: 'est_units',
  };

  const getStatsActiveTabPC = (metric: string): string => {
    const activeTabsMapping: {
      [key: string]: string;
    } = {
      ...pcConsoleActiveTabsMapping,
      PCU: 'pcu',
      EstUnits: 'est_units',
    };
    return activeTabsMapping[metric] ?? metric?.toLocaleLowerCase() ?? activeTabsMapping.PCU;
  };

  const getStatsActiveTabConsole = (metric: string): string => {
    const activeTabsMapping: {
      [key: string]: string;
    } = {
      ...pcConsoleActiveTabsMapping,
      LifetimePlayer: 'lifetime_player',
      CumulativeSalesofthelatestweek: 'japanSales',
      Salesofselectedtime: 'japanSales',
      'CumulativeRevenueofthelatestweek($)': 'japanSales',
    };
    return (
      activeTabsMapping[metric] ??
      metric?.toLocaleLowerCase() ??
      activeTabsMapping['DigitalRevenue($)']
    );
  };

  const getStatsActiveTab = (metric: string): string => {
    switch (entityType) {
      case 'mobile': {
        return getStatsActiveTabMobile(metric);
      }
      case 'pc': {
        return getStatsActiveTabPC(metric);
      }
      case PC_CONSOLE_COMBINE_ENTITY_TYPE:
      case 'console': {
        return getStatsActiveTabConsole(metric);
      }
      default: {
        return '';
      }
    }
  };

  // Helper function to get English metric key from localized title
  const getEnglishMetricKey = (localizedTitle: string, cardItem: any): string => {

    // Create reverse mapping from localized titles to English text
    const reverseLocaleMapping: { [key: string]: string } = {};
    
    // Helper function to build reverse mapping from DataBrain locale map
    const buildReverseMapping = (localeMap: { [key: string]: string }) => {
      Object.entries(localeMap).forEach(([englishText, localeKey]) => {
        try {
          const localizedText = formatMessage({ id: localeKey });
          if (localizedText && localizedText !== localeKey) {
            // Map localized text back to English text (not locale key)
            reverseLocaleMapping[localizedText] = englishText;
          }
        } catch (e) {
          // Ignore formatting errors
        }
      });
    };

    // Only build reverse mapping for DataBrain metrics since this is specifically for DataBrain cards navigation
    buildReverseMapping(METRICS_NAMES_LOCALE_KEYS_MAP);

    // Try to find English text from reverse mapping
    const englishText = reverseLocaleMapping[localizedTitle];
    if (englishText) {
      return englishText;
    }

    // Fallback to original title
    return localizedTitle;
  };

  // 点击卡片跳转 stats
  function jumpToStats(param: any, item: any) {
    const { titleContent, titleIcon, data_source: itemDataSource = '' } = item;
    // 优先使用 cardItem prop 中的 data_source 和 metricUserGuide，因为可能已经被替换为 gamalytic
    const actualDataSource = cardItem?.data_source || itemDataSource;
    const actualMetricUserGuide = cardItem?.metricUserGuide || item?.metricUserGuide;
    const { conditionDate: specialConditionDate } = specialJumpParams || {};
    const conditionDateNew = specialConditionDate || conditionDate || '';

    if (titleContent === 'PHeat') return;
    if (!titleContent) return;
    
    // Get English metric key for proper mapping
    const englishMetricKey = getEnglishMetricKey(titleContent, item);
    let activeTab = getStatsActiveTab(englishMetricKey?.replaceAll(' ', ''));
    
    if (titleIcon === 'gsd') {
      activeTab = 'gsd';
    }

    // detailFilterExtend, setDetailFilterExtend
    setDetailFilterExtend({
      ...detailFilterExtend,
      rangeTime: {
        type: toDateTypeFilter[conditionDateNew] || detailFilterExtend?.rangeTime?.type,
        value: ['', ''],
      },
    });

    let tpchartJumpParam = {
      timeType: toDateTypeFilter[conditionDateNew],
      startDate: '',
      endDate: '',
      regionType: conditionMarket?.regionType || 'region',
      regions: conditionMarket?.regions || ['global'],
      activeTab,
      from: 'overview',
    };

    // 如果卡片有target_segment
    if (item?.targetSegment) {
      tpchartJumpParam = {
        ...tpchartJumpParam,
        targetSegment: item?.targetSegment,
      } as any;
    }
    if (!isTrueActiveSegmentCard) {
      dispatch({
        type: 'dbiTopcharts/setTopchartJumpParam',
        payload: tpchartJumpParam,
      });
    }

    let tabName = 'stats';
    let subTab = '';
    switch (activeTab) {
      case 'hoursWatched':
        tabName = 'streaming';
        subTab = 'hoursWatched';
        break;
      case 'peackCCV':
        tabName = 'streaming';
        subTab = 'peackCCV';
        break;
      case 'airtime':
        tabName = 'streaming';
        subTab = 'airtime';
        break;
      case 'rank':
        tabName = 'storeRank';
        break;
    }

    // 判断是否为 gamalytic 来源的指标
    const isGamalyticSource = actualDataSource === 'gamalytic' || actualMetricUserGuide?.startsWith('gamalytic__');
    const isAlineaSource = actualDataSource === 'alinea' || actualMetricUserGuide?.startsWith('alinea__');
    const isSourceSteamSource = actualDataSource === 'source_steam' || actualMetricUserGuide?.startsWith('source_steam__');

    if (tabName === 'stats') {
      subTab = JUMPTO_KPITRENDS_MAMP[activeTab] || '';
      // 如果是 gamalytic 指标，直接使用完整的 metricUserGuide 作为 subTab
      if (isGamalyticSource && actualMetricUserGuide) {
        subTab = actualMetricUserGuide;
      }
      // 如果是 alinea 指标，同样使用完整的 metricUserGuide 作为 subTab
      if (isAlineaSource && actualMetricUserGuide) {
        subTab = actualMetricUserGuide;
        activeTab = actualMetricUserGuide;
      }
      // 如果是 source_steam 指标，与 alinea 同思路：用完整 metric 作为 subTab / activeSegment
      if (isSourceSteamSource && actualMetricUserGuide) {
        subTab = actualMetricUserGuide;
        activeTab = actualMetricUserGuide;
      }
    }

    if (tabName === 'storeRank') {
      subTab = JUMPTO_PCCONSOLESTORERANK_MAMP[actualDataSource] || '';
    }
    // console.log('activeTabactiveTab', activeTab);
    if (actualDataSource === 'ampere') {
      subTab = JUMPTO_KPITRENDS_APMERE_MAMP[activeTab] || '';
    }

    let pathGame = getDetailGamePathCommon({
      entityType,
      gameId,
      tab: tabName,
      subTab,
    });

    if (!!query) {
      const searchSplitterIdx = pathGame?.lastIndexOf('?') ?? -1;
      const search = searchSplitterIdx === -1 ? '' : pathGame?.substring(searchSplitterIdx + 1);
      const path = searchSplitterIdx === -1 ? pathGame : pathGame?.substring(0, searchSplitterIdx);
      const searchParams = new URLSearchParams(search);
      Object.entries(query).forEach((entry) => {
        const [key, value] = entry || [];
        searchParams.set(key, encodeURIComponent(value));
      });
      
      // 处理 gamalytic 来源的指标，需要在 query 中添加 metrics 参数
      if (isGamalyticSource && actualMetricUserGuide) {
        const metricsValue = `gamalytic::${actualMetricUserGuide}`;
        searchParams.set('metrics', encodeURIComponent(metricsValue));
      }
      // 处理 alinea 来源的指标，与 gamalytic 一致，在 query 中带上 metrics 参数
      if (isAlineaSource && actualMetricUserGuide) {
        const metricsValue = `alinea::${actualMetricUserGuide}`;
        searchParams.set('metrics', encodeURIComponent(metricsValue));
      }
      // 处理 source_steam 来源的指标，与 gamalytic / alinea 一致
      if (isSourceSteamSource && actualMetricUserGuide) {
        const metricsValue = `source_steam::${actualMetricUserGuide}`;
        searchParams.set('metrics', encodeURIComponent(metricsValue));
      }

      pathGame = `${path}?${searchParams.toString()}`;
    } else if (isGamalyticSource && actualMetricUserGuide) {
      // 即使没有 query，如果是 gamalytic 来源也需要添加 metrics 参数
      const metricsValue = `gamalytic::${actualMetricUserGuide}`;
      const searchParams = new URLSearchParams();
      searchParams.set('metrics', encodeURIComponent(metricsValue));
      pathGame = `${pathGame}?${searchParams.toString()}`;
    } else if (isAlineaSource && actualMetricUserGuide) {
      // 即使没有 query，如果是 alinea 来源也带上 metrics 参数
      const metricsValue = `alinea::${actualMetricUserGuide}`;
      const searchParams = new URLSearchParams();
      searchParams.set('metrics', encodeURIComponent(metricsValue));
      pathGame = `${pathGame}?${searchParams.toString()}`;
    } else if (isSourceSteamSource && actualMetricUserGuide) {
      // 即使没有 query，如果是 source_steam 来源也带上 metrics 参数
      const metricsValue = `source_steam::${actualMetricUserGuide}`;
      const searchParams = new URLSearchParams();
      searchParams.set('metrics', encodeURIComponent(metricsValue));
      pathGame = `${pathGame}?${searchParams.toString()}`;
    }

    setActiveId?.(tabName);
    dispatch({ type: 'dbiTopcharts/setIsOperation', payload: false });
    setActiveSegment?.(activeTab);
    pathGame && history.replace(pathGame);
  }

  // 公司详情页 Summary 页签 Game Performance Summary 模块的路由跳转
  const jumpToGamesStats = (param: any, item: any) => {
    const { conditionDate = 'day', lineData, segment, subSegment } = item ?? {};
    const { xAxisData: dates } = lineData ?? {};
    const path = getCompanyDetailsPath({
      companyId: gameId,
      tab: 'GamesStats',
      segment,
      subSegment,
    });
    let filterExtendTmp = { ...filterExtend };
    // 处理级联国家筛选器继承逻辑，不按照当前页面选择的国家，需要加业务判断 强行修改为 global
    if (['pc', 'console', PC_CONSOLE_COMBINE_ENTITY_TYPE].includes(entityType)) {
      const filterRegionKey = filterKeyMap.REGION;
      const filterExtendRegionTmp = filterExtendTmp[filterRegionKey];
      const regions = filterExtendRegionTmp?.regions;
      const isSetGlobal = !(
        regions?.includes('gb') ||
        regions?.includes('fr') ||
        regions?.includes('us') ||
        regions?.includes('de') ||
        regions?.includes('gb')
      );
      if (isSetGlobal) {
        filterExtendTmp = {
          ...filterExtendTmp,
          [filterRegionKey]: {
            ...filterExtendRegionTmp,
            filterType: 'market',
            filterRegions: [],
            filterMarkets: ['global'],
            region_type: 'region',
            regions: ['global'],
          },
        };
      }
    }
    // 处理时间继承，页面上没有时间筛选器，要根据卡片数据的时间，来设置继承的时间
    const filterTimeRangeKey = filterKeyMap.RANGE_TIME;
    const granularity = (() => {
      if (conditionDate === 'day') return 'daily';
      if (conditionDate === 'week') return 'weekly';
      if (conditionDate === 'month') return 'monthly';
      return 'daily';
    })();
    const timeType = (() => {
      if (conditionDate === 'day') return 'day';
      if (conditionDate === 'week') return 'week';
      if (conditionDate === 'month') return 'month';
      return 'day';
    })();
    const startDate = dates?.[0] ?? '';
    const endDate = dates?.[dates?.length - 1] ?? '';
    filterExtendTmp = {
      ...filterExtendTmp,
      [filterTimeRangeKey]: {
        ...filterExtendTmp[filterTimeRangeKey],
        filterType: conditionDate === 'day' ? 'date' : conditionDate,
        filterValue: [moment(startDate), moment(endDate)],
        time_type: timeType,
        granularity,
        start_date: startDate,
        end_date: endDate,
      },
    };
    setFilterExtend(filterExtendTmp);
    history.replace(path);
  };

  const jumpPage = (param: any, item: any) => {
    if (burriedPointLogging && gameId && gameName) {
      burriedPointLogging(item, gameId, gameName);
    }

    // 有传入跳转链接，直接跳转（例：KPIOverview All Reviews 跳转舆情卡片）
    if (linkUrl) {
      history.push(linkUrl);
      return;
    }

    // 如外部有传入跳转方法则使用传入跳转
    if (onClickJump) {
      onClickJump();
      return;
    }

    if (moduleName === 'companies') {
      // 公司详情页 Summary 页签 Game Performance Summary 模块 无数据不跳转页面
      if (noDataCondition) return;
      jumpToGamesStats(param, item);
      return;
    }

    jumpToStats(param, item);
  };

  const isDisabled = moduleName === 'companies' && noDataCondition;

  const getMetricTitleLocaleKey = (metricName: string): string => {
    const dataSource = data_source ? data_source : cardItem?.data_source;

    // Gamalytic：Overview 卡片 titleContent 会是 "Gamalytic Pcu" 这类英文展示文本，
    // 不能用 METRICS_NAMES_LOCALE_KEYS_MAP 直接查；需要参考 originalStats 的做法：
    // 用后端返回的 metricUserGuide（gamalytic__xxx）映射到 i18n key，再 formatMessage。
    const metricUserGuide = cardItem?.metricUserGuide;
    if (
      dataSource === 'gamalytic' ||
      (typeof metricUserGuide === 'string' && metricUserGuide.startsWith('gamalytic__'))
    ) {
      const gamalyticMetricLocaleKeyMap: Record<string, string> = {
        // Steam PCU/ACU
        'gamalytic__pcu': 'dbi_metric_gamalytic_steam_pcu',
        'gamalytic__acu': 'dbi_metric_gamalytic_steam_acu',

        // Followers/Reviews
        'gamalytic__steamfollowers': 'dbi_metric_gamalytic_steam_followers',
        'gamalytic__steamreviews': 'dbi_metric_gamalytic_steam_reviews',

        // Sales/Revenue/Units
        'gamalytic__est_revenue': 'dbi_metric_gamalytic_est_revenue',
        'gamalytic__est_units': 'dbi_metric_gamalytic_est_units',
        'gamalytic__est_cumulative_revenue': 'dbi_metric_gamalytic_steam_cumulative_premium_revenue',
        'gamalytic__est_cumulative_units': 'dbi_metric_gamalytic_steam_cumulative_units',

        // Price
        'gamalytic__est_price': 'dbi_metric_gamalytic_est_price',
        'gamalytic__weighted_price': 'dbi_metric_gamalytic_weighted_price',
        'gamalytic__lifetime_weighted_price': 'dbi_metric_gamalytic_lifetime_weighted_price_trends',

        // Wishlist
        'gamalytic__wishlists': 'dbi_metric_gamalytic_steam_daily_wishlist',
        'gamalytic__wishlists_total': 'dbi_metric_gamalytic_steam_lifetime_wishlist',

        // Other
        'gamalytic__players_by_country': 'dbi_metric_gamalytic_players_by_country',
        'gamalytic__playtime': 'dbi_metric_gamalytic_playtime_steam',
      };

      const localeKey =
        typeof metricUserGuide === 'string' ? gamalyticMetricLocaleKeyMap[metricUserGuide] : '';
      return localeKey ? formatMessage({ id: localeKey }) : metricName;
    }

    // source_steam：与 gamalytic 同思路，映射到 _locales/*/metricSubValue.ts 已有的 key 上，避免标题渲染成 startCase 后的 "Source Steam Pcu"。
    if (
      dataSource === 'source_steam' ||
      (typeof metricUserGuide === 'string' && metricUserGuide.startsWith('source_steam__'))
    ) {
      const sourceSteamMetricLocaleKeyMap: Record<string, string> = {
        source_steam__pcu: 'dbi_metric_source_steam_pcu',
        source_steam__acu: 'dbi_metric_source_steam_acu',
      };

      const localeKey =
        typeof metricUserGuide === 'string'
          ? sourceSteamMetricLocaleKeyMap[metricUserGuide]
          : '';
      return localeKey ? formatMessage({ id: localeKey }) : metricName;
    }

    if (
      dataSource === 'alinea' ||
      (typeof metricUserGuide === 'string' && metricUserGuide.startsWith('alinea__'))
    ) {
      const alineaMetricLocaleKeyMap: Record<string, string> = {
        alinea__pcu: 'dbi_metric_alinea_steam_pcu',
        alinea__acu: 'dbi_metric_alinea_steam_acu',
        alinea__steamfollowers: 'dbi_metric_alinea_steam_followers',
        alinea__steamreviews: 'dbi_metric_alinea_steam_reviews',
        alinea__est_revenue: 'dbi_metric_alinea_est_revenue',
        alinea__est_units: 'dbi_metric_alinea_est_units',
        alinea__est_cumulative_revenue: 'dbi_metric_alinea_steam_cumulative_premium_revenue',
        alinea__est_cumulative_units: 'dbi_metric_alinea_steam_cumulative_units',
        alinea__est_price: 'dbi_metric_alinea_est_price',
        alinea__weighted_price: 'dbi_metric_alinea_weighted_price',
        alinea__lifetime_weighted_price: 'dbi_metric_alinea_lifetime_weighted_price_trends',
        alinea__wishlists: 'dbi_metric_alinea_steam_daily_wishlist',
        alinea__wishlists_total: 'dbi_metric_alinea_steam_lifetime_wishlist',
        alinea__players_by_country: 'dbi_metric_alinea_players_by_country',
        alinea__playtime: 'dbi_metric_alinea_playtime_steam',
        alinea__ps_est_revenue: 'dbi_metric_alinea_ps_revenue',
        alinea__ps_est_cumulative_revenue: 'dbi_metric_alinea_ps_revenue_total',
        alinea__ps_est_units: 'dbi_metric_alinea_ps_units',
        alinea__ps_est_cumulative_units: 'dbi_metric_alinea_ps_units_total',
        alinea__ps_players: 'dbi_metric_alinea_ps_lifetime_players',
        alinea__ps_players_total: 'dbi_metric_alinea_ps_plus_total_players',
        alinea__ps_game_pass_players: 'dbi_metric_alinea_ps_game_pass_players',
        alinea__ps_game_pass_players_total: 'dbi_metric_alinea_ps_game_pass_players_total',
        alinea__ps_dau: 'dbi_metric_alinea_ps_dau',
        alinea__ps_mau: 'dbi_metric_alinea_ps_mau',
        alinea__ps_price: 'dbi_metric_alinea_ps_price',
        alinea__xbox_est_revenue: 'dbi_metric_alinea_xbox_revenue',
        alinea__xbox_est_cumulative_revenue: 'dbi_metric_alinea_xbox_revenue_total',
        alinea__xbox_est_units: 'dbi_metric_alinea_xbox_units',
        alinea__xbox_est_cumulative_units: 'dbi_metric_alinea_xbox_units_total',
        alinea__xbox_players: 'dbi_metric_alinea_xbox_players',
        alinea__xbox_players_total: 'dbi_metric_alinea_xbox_game_pass_total_players',
        alinea__xbox_game_pass_players: 'dbi_metric_alinea_xbox_game_pass_players',
        alinea__xbox_game_pass_players_total: 'dbi_metric_alinea_xbox_game_pass_players_total',
        alinea__xbox_dau: 'dbi_metric_alinea_xbox_dau',
        alinea__xbox_mau: 'dbi_metric_alinea_xbox_mau',
        alinea__xbox_price: 'dbi_metric_alinea_xbox_price',
      };

      const localeKey =
        typeof metricUserGuide === 'string'
          ? alineaMetricLocaleKeyMap[metricUserGuide]
          : '';
      return localeKey ? formatMessage({ id: localeKey }) : metricName;
    }

    let metricNameLocaleKey = '';
    switch (dataSource) {
      case 'gsd':
        metricNameLocaleKey = GSD_METRICS_NAMES_LOCALE_KEYS_MAP[metricName];
        break;
      case 'steam top sellers':
        metricNameLocaleKey = STEAM_TOP_SELLERS_NAMES_LOCALE_KEYS_MAP[metricName];
        break;
      case 'steam top wish lists':
        metricNameLocaleKey = STEAM_TOP_WISH_LISTS_NAMES_LOCALE_KEYS_MAP[metricName];
        break;
      case 'custom_overview_card':
        metricNameLocaleKey = CUSTOM_OVERVIEW_CARD_NAMES_LOCALE_KEYS_MAP[metricName];
        break;
      case 'US Revenue & Units':
        metricNameLocaleKey = USREVENUE_CARD_NAMES_LOCALE_KEYS_MAP[metricName];
        break;
      default:
        metricNameLocaleKey = METRICS_NAMES_LOCALE_KEYS_MAP[metricName];
    }
    return metricNameLocaleKey && metricNameLocaleKey !== ''
      ? formatMessage({ id: metricNameLocaleKey })
      : metricName;
  };

  const metricTitle = getMetricTitle(titleContent, moduleName);
  const metricTitleLocaleText = getMetricTitleLocaleKey(metricTitle);

  // const renderNorm = (item: any) => {
  //   const { percent = '', percent_int = 0, value = 0, platform = '' } = item || {};
  //   return (
  //     <div className="community-render-norm">
  //       <p className="dbi-card-progress-p">
  //         {<span className="dbi-card-progress-textleft">{platform}</span>}
  //         {
  //           <span className="dbi-card-progress-textright">
  //             {`${financeFormat(value, 2, false, '')}(${percent})`}
  //           </span>
  //         }
  //       </p>
  //       <Progress
  //         className="dbo-video-progress"
  //         percent={percent_int}
  //         strokeWidth={4}
  //         strokeColor={getStrokeColor(platform)}
  //       />
  //     </div>
  //   );
  // };

  const renderCardContent = () => {
    // 如果外部有传入自定义卡片内容，则优化展示自定义内容
    if (customCardContent) {
      return customCardContent();
    }

    // 满足无数据展示条件，内容区空态展示
    if (noDataCondition) {
      return (
        <div
          className={classnames('context-nodata', {
            // 展示 platform 信息时，无数据，高度控制
            'show-platform-no-data': ifShowPlatform && !platformListCur?.length,
          })}
        >
          {noDataText ?? formatMessage({
            id:
              data_source === 'gsd' && conditionDate !== 'week'
                ? 'dbi_no_data_available_gsd'
                : 'dbi_no_data_available',
          })}
        </div>
      );
    }

    return (
      <>
        <div className="context-num">
          <div className="context-num-left">
            {/* overview 卡片数值内容区域 */}
            {isShowLine && (
              <CardValueContent
                titleContent={titleContent}
                numArrowData={numArrowData}
                baseIndicators={baseIndicators}
                rankData={rankData}
                rankLoading={rankLoading}
                cardItem={cardItem}
                metricTitleLocaleText={metricTitleLocaleText}
                dateType={conditionDate}
                isRatioValueFormat={isRatioValueFormat}
                isShowRateTips={isShowRateTips}
                isShowRank={isShowRank}
                // isRatioValueFormatNoPrecent={isRatioValueFormatNoPrecent}
              />
            )}
          </div>
          {/* overview 卡片可视化内容区域 */}
          {isShowLine ? (
            <div className="context-data">
              <div className={`context-summary-line`}>
                {titleContent === 'PHeat' ? (
                  <>
                    <PheatChart rankData={rankData} />
                  </>
                ) : (
                  <CardLineChart
                    yAxisInverse={yAxisInverse}
                    yAxisMin={yAxisMin}
                    yAxisFormatter={yAxisFormatter}
                    enableSeriesAreaStyle={enableSeriesAreaStyle}
                    lineDom={lineDom}
                    lineData={lineData}
                    titleContent={metricTitleLocaleText}
                    metricTitleLocaleKey={metricTitleLocaleKey}
                    baseIndicators={baseIndicators}
                    rankData={rankData}
                    icon={icon}
                    title={titleContent}
                    moduleName={moduleName}
                    isPolishing={isPolishing}
                    isAdustTooltipPosition={isAdustTooltipPosition}
                    isRatioValueFormat={isRatioValueFormat}
                    // isRatioValueFormatNoPrecent={isRatioValueFormatNoPrecent}
                    noSourcePlatform={true}
                    fullLabelBreakpoint={fullLabelBreakpoint}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="context-nodata-right">{formatMessage({ id: 'dbi_no_available' })}</div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <div
        className={classnames('dbi-card-context', className, {
          'no-pointer-cursor': isDisabled,
          'dbi-card-context-showplatform': ifShowPlatform,
          'dbi-card-context-showplatform-no-data': noDataCondition && !platformListCur?.length,
        })}
        onClick={() => jumpPage(index, cardItem)}
      >
        {/* overview 卡片标题区域 */}
        <CardTitleContent
          titleIcon={titleIcon}
          titleContent={titleContent}
          baseIndicators={baseIndicators}
          numArrowData={numArrowData}
          lineData={lineData}
          rankData={rankData}
          data_source={data_source}
          index={index}
          moduleName={moduleName}
          entityType={entityType}
          dispatch={dispatch}
          cardItem={cardItem}
          customBubbleContent={customBubbleContent}
          aggregationBubbleWidth={aggregationBubbleWidth}
          isShowDataSourceIcon={isShowDataSourceIcon}
          metricTitle={metricTitle}
          metricTitleLocaleText={metricTitleLocaleText}
          link_url={linkUrl}
          isShowInfoIcon={isShowInfoIcon}
          tipsInfoText={tipsInfoText}
        />

        {/* overview 卡片主体内容区域 */}
        {renderCardContent()}
      </div>

      {/* US Revenue & Units 独有内容 */}
      {ifShowPlatform && !noDataCondition && <CardPlatformContent platformList={platformListCur} />}
    </>
  );
};

export default OverviewCard;
